/**
 * Safely render any value that might be a string, an address object, or null.
 * Order of address-like keys is chosen to match the most common forms across
 * the Dierend Firestore schema (some docs store {street,city,...}, others
 * store {line1,line2,city,postalCode,country}, etc).
 */
export const formatAddress = (value, fallback = '-') => {
  if (!value) return fallback;
  if (typeof value === 'string') return value || fallback;
  if (typeof value !== 'object') return String(value);
  const parts = [
    value.line1,
    value.line2,
    value.street,
    value.city,
    value.state,
    value.zip,
    value.postalCode,
    value.country,
    value.formattedAddress,
  ];
  const joined = parts.filter((p) => typeof p === 'string' && p.trim()).join(', ');
  return joined || fallback;
};

/**
 * Render any potentially-object value as a safe React child.
 * - strings/numbers pass through
 * - null/undefined → fallback
 * - objects → JSON.stringify (one line, capped)
 */
export const safeText = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      try {
        return value.toDate().toLocaleString();
      } catch {
        return fallback;
      }
    }
    try {
      const json = JSON.stringify(value);
      return json.length > 200 ? `${json.slice(0, 200)}…` : json;
    } catch {
      return fallback;
    }
  }
  return String(value);
};

const helpers = { formatAddress, safeText };
export default helpers;
